import { useEffect, useRef, useState } from "react";
import { Paperclip, Send, X, Smile } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import EmojiPicker, { EmojiStyle, Theme } from "emoji-picker-react";

const ACCEPT =
  "image/*,video/*,application/pdf,application/msword," +
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
  "application/vnd.ms-excel," +
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet," +
  "application/zip,text/plain";

/** Multi-line chat composer.
 *  - Enter → send (via onSend)
 *  - Shift+Enter → newline
 *  - Auto-grows height up to 6 rows
 *  - Handles clipboard paste of images
 *  - Exposes hidden file picker for the paperclip button
 */
export default function ChatComposer({
  value,
  onChange,
  onSend,
  onFiles,       // (fileList) => void   — multi-file supported
  onTyping,
  disabled,
  mentionables = [],  // [{id, full_name}] — enables @-tagging when non-empty
  placeholder = "Type a message… (Shift+Enter for new line)",
}) {
  const taRef = useRef(null);
  const fileRef = useRef(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [caret, setCaret] = useState(0);
  const [mentionIdx, setMentionIdx] = useState(0);
  const [mentionDismissed, setMentionDismissed] = useState(false);

  // Active "@query" immediately before the caret (query has no spaces).
  const mentionMatch = mentionables.length
    ? /(^|\s)@(\w*)$/.exec((value || "").slice(0, caret))
    : null;
  const mentionQuery = mentionMatch ? mentionMatch[2].toLowerCase() : null;
  const mentionOptions = mentionQuery === null || mentionDismissed ? [] : mentionables
    .filter((m) => !mentionQuery || m.full_name.toLowerCase().split(/\s+/).some((w) => w.startsWith(mentionQuery)) || m.full_name.toLowerCase().startsWith(mentionQuery))
    .slice(0, 6);
  useEffect(() => { setMentionIdx(0); }, [mentionQuery]);

  const pickMention = (m) => {
    const before = (value || "").slice(0, caret);
    const after = (value || "").slice(caret);
    const start = before.lastIndexOf("@");
    const inserted = `@${m.full_name} `;
    onChange(before.slice(0, start) + inserted + after);
    setMentionDismissed(false);
    requestAnimationFrame(() => {
      const ta = taRef.current;
      if (!ta) return;
      const pos = start + inserted.length;
      ta.focus();
      ta.setSelectionRange(pos, pos);
      setCaret(pos);
    });
  };

  // Auto-grow textarea up to max ~144px (approx 6 rows).
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 144)}px`;
  }, [value]);

  const insertEmoji = (emoji) => {
    const ta = taRef.current;
    if (!ta) {
      onChange((value || "") + emoji);
      return;
    }
    const start = ta.selectionStart ?? value.length;
    const end = ta.selectionEnd ?? value.length;
    const next = (value || "").slice(0, start) + emoji + (value || "").slice(end);
    onChange(next);
    // Restore caret after React re-renders.
    requestAnimationFrame(() => {
      try {
        ta.focus();
        const pos = start + emoji.length;
        ta.setSelectionRange(pos, pos);
      } catch { /* noop */ }
    });
  };

  const onKeyDown = (e) => {
    if (mentionOptions.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setMentionIdx((i) => (i + 1) % mentionOptions.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setMentionIdx((i) => (i - 1 + mentionOptions.length) % mentionOptions.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pickMention(mentionOptions[mentionIdx]); return; }
      if (e.key === "Escape") { e.preventDefault(); setMentionDismissed(true); return; }
    }
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (value.trim()) onSend();
    }
  };

  const onPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const it of items) {
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      onFiles(files);
    }
  };

  return (
    <div className="relative p-3 border-t border-border/60 flex items-end gap-2 bg-background/80 backdrop-blur-sm">
      {mentionOptions.length > 0 && (
        <div data-testid="mention-popup" className="absolute left-14 bottom-full mb-1 z-40 w-64 rounded-lg border border-border bg-popover text-popover-foreground shadow-lg overflow-hidden">
          {mentionOptions.map((m, i) => (
            <button
              key={m.id}
              type="button"
              data-testid={`mention-opt-${m.id}`}
              onMouseDown={(e) => { e.preventDefault(); pickMention(m); }}
              className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${i === mentionIdx ? "bg-accent" : "hover:bg-accent/60"}`}
            >
              <span className="text-primary">@</span>
              <span className="truncate">{m.full_name}</span>
              {m.designation && <span className="ml-auto text-[10px] text-muted-foreground truncate">{m.designation}</span>}
            </button>
          ))}
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        multiple
        className="hidden"
        accept={ACCEPT}
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length) onFiles(files);
          e.target.value = "";
        }}
      />
      <Button
        data-testid="chat-attach"
        variant="ghost"
        size="icon"
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={disabled}
      >
        <Paperclip className="w-4 h-4" />
      </Button>
      <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
        <PopoverTrigger asChild>
          <Button
            data-testid="chat-emoji"
            variant="ghost"
            size="icon"
            type="button"
            disabled={disabled}
            title="Insert emoji"
          >
            <Smile className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          side="top"
          align="end"
          sideOffset={8}
          className="p-0 border-0 bg-transparent shadow-none w-auto"
          data-testid="chat-emoji-popover"
        >
          <EmojiPicker
            onEmojiClick={(e) => insertEmoji(e.emoji)}
            theme={typeof document !== "undefined" && document.documentElement.classList.contains("dark") ? Theme.DARK : Theme.LIGHT}
            emojiStyle={EmojiStyle.NATIVE}
            width={320}
            height={380}
            searchDisabled={false}
            skinTonesDisabled
            previewConfig={{ showPreview: false }}
            lazyLoadEmojis
          />
        </PopoverContent>
      </Popover>
      <textarea
        ref={taRef}
        data-testid="chat-input"
        value={value}
        onChange={(e) => { onChange(e.target.value); setCaret(e.target.selectionStart ?? e.target.value.length); setMentionDismissed(false); onTyping?.(); }}
        onKeyUp={(e) => { if (!["Enter", "ArrowDown", "ArrowUp", "Tab", "Escape"].includes(e.key)) setCaret(e.target.selectionStart ?? 0); }}
        onClick={(e) => setCaret(e.target.selectionStart ?? 0)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        placeholder={placeholder}
        disabled={disabled}
        rows={1}
        className="flex-1 resize-none rounded-2xl bg-background border border-border/60 px-4 py-2.5 text-sm leading-snug outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/60 disabled:opacity-60 min-h-[40px] max-h-[144px]"
      />
      <Button
        data-testid="chat-send"
        type="button"
        size="icon"
        onClick={onSend}
        disabled={!value.trim() || disabled}
        className="rounded-full"
      >
        <Send className="w-4 h-4" />
      </Button>
    </div>
  );
}

export function DropOverlay({ visible }) {
  if (!visible) return null;
  return (
    <div
      data-testid="chat-drop-overlay"
      className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none"
    >
      <div className="absolute inset-3 rounded-xl border-2 border-dashed border-primary bg-primary/10 backdrop-blur-sm flex items-center justify-center">
        <div className="text-primary font-medium flex items-center gap-2">
          <Paperclip className="w-5 h-5" />
          Drop files to send
        </div>
      </div>
    </div>
  );
}

export function UploadingList({ items, onRemove }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="px-3 py-2 border-t border-border/60 flex items-center gap-2 flex-wrap bg-accent/40">
      {items.map((it) => (
        <div key={it.id} className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-background border border-border/60 text-xs">
          {it.status === "error" ? (
            <span className="text-red-500">✕</span>
          ) : it.status === "uploading" ? (
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
          ) : (
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
          )}
          <span className="max-w-[160px] truncate">{it.file.name}</span>
          <button onClick={() => onRemove(it.id)} className="opacity-60 hover:opacity-100">
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
    </div>
  );
}
